import { useCallback, useMemo } from "react";

import { AxiosError } from "axios";

import usePlatformEnv from "./usePlatformEnv";

import { useToast } from "./index";
import { useTeamStore } from "../contexts";

import { useTeamApiService } from "../services";
import type {
	EntityRoles,
	FullTeamModel,
	IgnoreStateUpdate,
	LogoModel,
	MatchStatusEnum,
	PaginationParamsModel
} from "../types";

const useTeam = () => {
	const teamService = useTeamApiService();

	const { setState, setInitial, ...store } = useTeamStore();

	const { showToast } = useToast();

	const { isShowRoleToasts } = usePlatformEnv();

	const methods = useMemo(() => {
		return {
			getTeamOverview: async ({ id, ignoreLoading, ignoreStateUpdate }: { id: string } & IgnoreStateUpdate) => {
				try {
					if (!ignoreLoading) {
						setState({ loading: true });
					}
					const overview = await teamService.getTeamOverview(id);
					if (!ignoreStateUpdate) {
						setState({ overview });
					}
					return overview;
				} catch {
					showToast({
						text: "Team couldn't be found",
						noIcon: true
					});
					return null;
				} finally {
					if (!ignoreLoading) {
						setState({ loading: false });
					}
				}
			},
			updateTeam: async (
				data: Partial<Omit<FullTeamModel, "logo">> & {
					logo?: LogoModel | string | null;
					teamId: string;
				},
				dontShowToast?: boolean
			) => {
				setState({ loading: true });

				try {
					const { team } = await teamService.updateTeam(data);

					setState(ctx => ({ overview: { ...ctx.overview, ...team } }));

					!dontShowToast && showToast({ text: "Team updated successfully", noIcon: true });

					return team;
				} catch (error) {
					showToast({ text: "Error updating team", noIcon: true });
					console.log("update failed", error);
					return null;
				} finally {
					setState({ loading: false });
				}
			},
			findTeams: async (search?: string, customParams?: Record<string, any>) => {
				const { teams } = await teamService.findTeams(search, customParams);
				return teams;
			},
			getFollowedTeams: async (params: { search?: string } & PaginationParamsModel) => {
				setState({ loadingFollowedTeams: true });
				const { teams, nextToken } = await teamService.getFollowedTeams(params);
				setState({ followedTeamsNextToken: nextToken, loadingFollowedTeams: false });
				return teams;
			},
			getPlayers: async ({
				teamId,
				limit = 10,
				before,
				after,
				ignoreLoading,
				ignoreStateUpdate
			}: { teamId: string } & PaginationParamsModel & IgnoreStateUpdate) => {
				if (!ignoreLoading) {
					setState({ loadingPlayers: true });
				}
				if (!after && !ignoreStateUpdate) {
					setState({ players: [], nextListingPlayersToken: null });
				}
				try {
					const { team, nextToken } = await teamService.getPlayers({ limit, before, after, teamId });
					if (!ignoreStateUpdate) {
						setState(ctx => ({
							nextListingPlayersToken: nextToken,
							players: !!before || !!after ? ctx.players : team.players || []
						}));
					}

					return {
						nextToken,
						players: team.players || []
					};
				} catch (err) {
					console.error(err);
					if (err instanceof Error && err.cause !== AxiosError.ERR_CANCELED) {
						showToast({
							text: "Couldn't fetch team players.",
							noIcon: true
						});
					}
					if (!ignoreStateUpdate) {
						setState({ nextListingPlayersToken: null });
					}

					return {
						nextToken: null,
						players: []
					};
				} finally {
					if (!ignoreLoading) {
						setState({ loadingPlayers: false });
					}
				}
			},
			getTeamMatches: async ({
				limit = 10,
				after,
				...rest
			}: { teamId: string; filters?: MatchStatusEnum[] } & PaginationParamsModel) => {
				setState({ loadingTeamMatches: true });
				if (!after) {
					setState({ teamMatches: [], nextListingMatchesToken: null });
				}

				try {
					const { matches, afterToken } = await teamService.getTeamMatches({ limit, after, ...rest });
					setState(ctx => ({
						teamMatches: !!after ? [...ctx.teamMatches, ...matches] : matches,
						nextListingMatchesToken: afterToken
					}));
					return matches;
				} catch (err) {
					console.error(err);
					showToast({ text: "Something went wrong", noIcon: true });
					setState({ nextListingMatchesToken: null });
				} finally {
					setState({ loadingTeamMatches: false });
				}
			},
			getTeamCompetitions: async (teamId: string) => {
				setState({ loadingTeamCompetitions: true });
				const { team } = await teamService.getTeamCompetitions(teamId);
				setState({ loadingTeamCompetitions: false });
				return team.competitions || [];
			},
			getTeamCompetitionsStats: async (teamId: string) => {
				setState({ loadingTeamCompetitionsStats: true });
				const { items } = await teamService.getTeamCompetitionsStats(teamId);
				setState({ loadingTeamCompetitionsStats: false });
				return items || [];
			},
			getTeamPlayersCompetitionStats: async (teamId: string, competitionId: string) => {
				setState({ loadingTeamPlayerCompetitionStats: true });
				const { items } = await teamService.getTeamPlayersCompetitionStats(teamId, competitionId);
				setState({ loadingTeamPlayerCompetitionStats: false });
				return items || [];
			},
			getTeamGroups: async (teamId: string) => {
				setState({ loadingTeamGroups: true });
				const { groups } = await teamService.getTeamGroups(teamId);
				setState({ loadingTeamGroups: false });
				return groups;
			},
			getTeamEvents: async (props: {
				teamId: string;
				matchStatus?: MatchStatusEnum;
				limit?: number;
				after?: string;
				before?: string;
				timestampGte?: string;
				timestampLt?: string;
			}) => {
				setState({ loadingTeamEvents: true });
				try {
					const { events, nextToken } = await teamService.getTeamEvents(props);

					setState(ctx => ({
						nextListingEventsToken: nextToken,
						teamEvents: !!props?.before || !!props?.after ? ctx.teamEvents : events
					}));

					return {
						nextToken,
						events
					};
				} catch (err) {
					console.error(err);
					showToast({ text: "Something went wrong", noIcon: true });
					setState({ nextListingEventsToken: null });

					return {
						nextToken: null,
						events: []
					};
				} finally {
					setState({ loadingTeamEvents: false });
				}
			},
			getTeamInfo: async (id: string, ignoreLoading?: boolean) => {
				!ignoreLoading && setState({ loadingInfo: true });

				try {
					const { team } = await teamService.getTeamInfo(id);

					setState(ctx => ({ loadingInfo: ignoreLoading ? ctx.loadingInfo : false }));

					if (team?.auth?.roles && isShowRoleToasts) {
						showToast({
							text: `Roles for team: ${team.auth.roles.join(", ")}`,
							noIcon: true,
							position: "bottom-left"
						});
					}

					return team;
				} catch {
					showToast({
						text: "Team couldn't be found",
						noIcon: true
					});
					return null;
				} finally {
					if (!ignoreLoading) {
						setState({ loadingInfo: false });
					}
				}
			},
			setUserTeamRoles: (userTeamRoles: EntityRoles[]) => {
				setState({ userTeamRoles });
			},
			resetStore() {
				setInitial();
			}
		};
	}, [setState, teamService, setInitial, showToast, isShowRoleToasts]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useTeam;
